import { useEffect, useRef, useState } from "react";
import { CatalogActionMeta, Category } from "../types";
import { ActionCard } from "./ActionCard";
import { CustomPromptHero } from "./CustomPromptHero";

interface Props {
  category: Category;
  actions: CatalogActionMeta[];
  onSelect: (actionId: string) => Promise<void> | void;
  /** Хэндлер для action.requiresPhoto=false — text-to-image submit */
  onCreate?: (prompt: string) => Promise<void> | void;
  onBack: () => void;
  busy?: boolean;
}

const COLLAPSE_AT = 80;

export function ActionList({ category, actions, onSelect, onCreate, onBack, busy = false }: Props) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const [createPrompt, setCreatePrompt] = useState("");
  const [vw, setVw] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 390,
  );

  // create-mode: ровно один action и он не требует фото → рендерим CustomPromptHero
  const isCreateMode = actions.length === 1 && actions[0].requiresPhoto === false;

  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    const onScroll = () => setScrollY(el.scrollTop);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Hero фиксированной высоты (vw/2). Сам уезжает вверх при скролле в общем
  // потоке — никаких изменений height/top, никакого jump'а контента.
  const heroH = Math.round(vw / 2);
  // Линейный прогресс 0..1 для синхронного crossfade title/compact-bar
  // с движением пальца. Без CSS-transition — pixel-perfect.
  const progress = Math.min(1, Math.max(0, scrollY / COLLAPSE_AT));
  const heroPhoto = category.previewUrl ?? "";

  const groups = new Map<string | null, CatalogActionMeta[]>();
  for (const a of actions) {
    const k = a.group ?? null;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(a);
  }
  const hasGroups =
    groups.size > 1 || (groups.size === 1 && [...groups.keys()][0] !== null);

  return (
    <div className="relative h-full overflow-hidden" style={{ background: "var(--color-sk-bg)" }}>
      {/* Единый scroll container: hero и sheet оба в обычном потоке. */}
      <div
        ref={sheetRef}
        className="absolute inset-0 overflow-y-auto overflow-x-hidden"
      >
        {/* Hero — обычный block, естественно уезжает вверх при скролле */}
        <div
          style={{
            position: "relative",
            height: heroH,
            background: "#000",
            overflow: "hidden",
          }}
        >
          {heroPhoto && (
            <img
              src={heroPhoto}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
            />
          )}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(14,13,12,1), rgba(14,13,12,0.1) 55%, rgba(14,13,12,0.5) 100%)",
            }}
          />
          {/* Large title — pinned bottom hero, fade out по мере скролла */}
          <div
            className="absolute"
            style={{
              left: 18,
              right: 18,
              bottom: 16,
              color: "#fff",
              opacity: 1 - progress,
            }}
          >
            <div
              style={{
                fontSize: 11,
                letterSpacing: 1,
                textTransform: "uppercase",
                fontWeight: 600,
                opacity: 0.7,
                marginBottom: 4,
                fontFamily: "var(--font-sk-mono)",
              }}
            >
              {actions.length} ВАРИАНТОВ ✦
            </div>
            <div
              style={{
                fontSize: 30,
                fontWeight: 600,
                letterSpacing: -0.5,
                fontFamily: "var(--font-sk-display)",
                fontStyle: "italic",
              }}
            >
              {category.emoji} {category.title}
            </div>
          </div>
        </div>

        {/* Sheet — обычный block, перекрывает hero на 24px через negative margin */}
        <div
          style={{
            background: "var(--color-sk-bg)",
            borderRadius: "24px 24px 0 0",
            marginTop: -24,
            padding: "20px 14px 24px",
            position: "relative",
            zIndex: 1,
            minHeight: `calc(100% - ${heroH - 24}px)`,
          }}
        >
          {isCreateMode ? (
            <CustomPromptHero
              variant="create"
              value={createPrompt}
              onChange={setCreatePrompt}
              onSubmit={(p) => onCreate?.(p)}
            />
          ) : (
          <div className="flex flex-col" style={{ gap: 10 }}>
            {[...groups.entries()].map(([groupName, items]) => (
              <div key={groupName ?? "_default"}>
                {hasGroups && groupName && (
                  <div
                    className="sticky"
                    style={{
                      top: 60,
                      background: "var(--color-sk-bg)",
                      padding: "14px 4px 8px",
                      fontFamily: "var(--font-sk-mono)",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      color: "var(--color-sk-text-dim)",
                      boxShadow: "0 1px 0 var(--color-sk-line)",
                      zIndex: 1,
                    }}
                  >
                    {groupName}
                  </div>
                )}
                <div className="flex flex-col" style={{ gap: 10 }}>
                  {items.map((a) => (
                    <ActionCard
                      key={a.id}
                      previewUrl={a.preview}
                      emoji={a.emoji}
                      title={a.title}
                      description={a.description}
                      onClick={() => onSelect(a.id)}
                      disabled={busy}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Compact top-bar overlay — fades in по мере скролла. Pointer-events: none
         чтобы не блокировать скролл и тапы по hero под ним. */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{
          height: 60,
          background: "rgba(14,13,12,0.85)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--color-sk-line)",
          opacity: progress,
          zIndex: 5,
        }}
      />

      {/* Back-button — всегда видна сверху-слева, поверх всего */}
      <button
        type="button"
        onClick={onBack}
        aria-label="Назад"
        className="absolute flex items-center justify-center"
        style={{
          top: 14,
          left: 14,
          width: 48,
          height: 48,
          borderRadius: 100,
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: "#fff",
          fontSize: 22,
          zIndex: 10,
        }}
      >
        ‹
      </button>

      {/* Compact title — fades in справа от back-кнопки */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: 22,
          left: 74,
          right: 18,
          color: "#fff",
          fontSize: 17,
          fontWeight: 600,
          letterSpacing: -0.3,
          fontFamily: "var(--font-sk-ui)",
          opacity: progress,
          zIndex: 10,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {category.emoji} {category.title}
      </div>
    </div>
  );
}
